import { ArrowRightIcon, CheckCircleIcon, XCircleIcon, XIcon } from '@phosphor-icons/react';
import { Link } from 'react-router-dom';
import useSWR from 'swr';
import toast from 'react-hot-toast';

import { Button } from 'components/button/Button';
import { getDisplayError } from '@utils/get-display-error';
import { Spinner } from 'components/Spinner';
import { formatDate } from '@utils/date';

import { getQuotationDrafts, deleteQuotationDraft } from '../models';
import type { QuotationDraftSummary } from '../endpoints/ExtractLanesEndpoint.types';

const StatusIndicator = ({ status }: { status: string }) => {
  switch (status) {
    case 'Pending':
    case 'Processing':
      return <Spinner size={4} color="primary" />;
    case 'Completed':
      return <CheckCircleIcon className="w-5 h-5 text-positive-text" weight="fill" />;
    case 'Failed':
      return <XCircleIcon className="w-5 h-5 text-negative-text" weight="fill" />;
    default:
      return null;
  }
};

const DraftCard = ({ draft, onDelete }: { draft: QuotationDraftSummary; onDelete: () => void }) => {
  return (
    <div className="flex items-center justify-between rounded-lg border border-border bg-surface px-4 py-3">
      <div className="flex items-center gap-3">
        <StatusIndicator status={draft.status} />
        <div>
          <div className="font-medium text-sm">{draft.name}</div>
          <div className="text-xs text-fg-muted">
            {draft.fileCount} bestand{draft.fileCount !== 1 ? 'en' : ''} &middot; {formatDate(draft.createdAt)}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <Link to={`ai-draft?draftId=${draft.id}`}>
          <Button color="primary" iconRight={<ArrowRightIcon className="button-icon" />}>
            Doorgaan
          </Button>
        </Link>
        <Button onTrigger={onDelete} iconLeft={<XIcon className="button-icon" />} />
      </div>
    </div>
  );
};

export const QuotationDraftBanner = () => {
  const hasProcessing = (data: { drafts: QuotationDraftSummary[] } | undefined) =>
    data?.drafts.some((d) => d.status === 'Pending' || d.status === 'Processing');

  const { data, mutate } = useSWR('quotation-drafts', () => getQuotationDrafts(), {
    refreshInterval: (data) => (hasProcessing(data) ? 5000 : 0),
  });

  const drafts = data?.drafts ?? [];
  if (drafts.length === 0) return null;

  const handleDelete = async (draftId: string) => {
    try {
      await deleteQuotationDraft(draftId);
      await mutate();
    } catch (err) {
      toast.error('Kon draft niet verwijderen: ' + getDisplayError(err));
    }
  };

  return (
    <div className="flex flex-col gap-2">
      {drafts.map((draft) => (
        <DraftCard key={draft.id} draft={draft} onDelete={() => handleDelete(draft.id)} />
      ))}
    </div>
  );
};
